'use client';

import {
  Suspense,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import { useOrgAuthInfo } from '@/hooks/useOrgAuthInfo';
import { SettingsTabProps } from '@/types/settings';
// The seven sub-tabs are dynamically imported below (next/dynamic): only one
// ever mounts at a time, but a static import here still shipped all seven in
// this tab's initial bundle.
import dynamic from 'next/dynamic';
import useOrganizationStore from '@/state/orgState';
import useUserStore from '@/state/userState';
import emptyHeaderObj from '@/types/emptyHeaderObj';
import User from '@/types/userType';
import { useUserLogged } from '@/hooks/useUserLogged';
import {
  useUpdateUserRole,
  useCreateTeam,
  useDeleteTeam,
  useAddUserToTeam,
  useRemoveUserFromTeam,
  useCreateUser,
  useDeleteUser,
} from '@/hooks/useTeamData';
import { useQuery } from '@tanstack/react-query';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import {
  fetchOrgMembers,
  NO_TEAM,
  ORG_MEMBERS_NEXT_LIMIT,
  type OrgMemberRow,
  type OrgTeamFacet,
} from '@/apis/fetchOrgMembers';
import { useSlackConfigured } from '@/hooks/useSlackData';
import { useDisclosure } from '@/hooks/useDisclosure';
import { EditUserModal } from '../../UserManagement/Modals/EditUserModal';
import { CreateTeamModal } from '../modals/CreateTeamModal';
import { TeamSelector } from './TeamSelector';
import { AddGitOrg } from '../../UserManagement/Modals/AddGitOrg';
import { TeamMembersModal } from '../../UserManagement/Modals/TeamMembersModal';
import { AddMemberModal } from '../../UserManagement/Modals/AddMemberModal';
import { useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useQueryState } from 'nuqs';
import { FaSync, FaPlus } from 'react-icons/fa';
import { SettingsHeader } from '../components/SettingsHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/useToast';
import { Button as ShadcnButton } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import {
  getTeamMembersFromGh,
  importTeamsFromGh,
  getTeamMembersFromGl,
  importTeamsFromGl,
} from '@/apis/importTeamsFromGitProvider';
import {
  getGithubOrgMembers,
  getGitlabGroupMembers,
} from '@/apis/importMembersFromGitProvider';
import syncedGitSource from '@/apis/syncedGitSource';
import { createTeam } from '@/apis/createTeam';
import { createUser } from '@/apis/ApiUtil';
import { signIn } from 'next-auth/react';
import { mountScope } from '@/components/Auth/utils';
import { useAuthStore } from '@/state/authState';
import { getProviderDisplayName } from '@/utils/getProviderDisplayName';
import { PencilSimple, Trash, Users } from '@phosphor-icons/react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from '@/components/ui/empty';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { assignableRoles, isAdminOrOwner, isOwner, ownerOnly, roleLabel } from '@/lib/rbac';
import {
  useTrialAccessStatus,
  useSetTrialAccess,
} from '@/hooks/useTrialAccess';
import { IntegrationSubTabSkeleton } from '@/components/Settings/IntegrationSubTabSkeleton';

const integrationSubTabLoading = () => <IntegrationSubTabSkeleton />;

const CursorTab = dynamic(() => import('./CursorTab'), {
  loading: integrationSubTabLoading,
});
const ClaudeTab = dynamic(() => import('./ClaudeTab'), {
  loading: integrationSubTabLoading,
});
const CodexTab = dynamic(() => import('./CodexTab'), {
  loading: integrationSubTabLoading,
});
const JiraLinearTab = dynamic(() => import('./JiraLinearTab'), {
  loading: integrationSubTabLoading,
});
const DevRevTab = dynamic(() => import('./DevRevTab'), {
  loading: integrationSubTabLoading,
});
const AsanaTab = dynamic(() => import('./AsanaTab'), {
  loading: integrationSubTabLoading,
});
const SlackMappingTab = dynamic(() => import('./SlackMappingTab'), {
  loading: integrationSubTabLoading,
});

/** Warm the JS chunk before the user clicks an integration sub-tab. */
const SUB_TAB_PRELOAD: Partial<Record<SubTabId, () => void>> = {
  cursor: () => void import('./CursorTab'),
  claude: () => void import('./ClaudeTab'),
  codex: () => void import('./CodexTab'),
  jira: () => void import('./JiraLinearTab'),
  devrev: () => void import('./DevRevTab'),
  asana: () => void import('./AsanaTab'),
  slack: () => void import('./SlackMappingTab'),
};

/*
 * How close to the bottom of the page paging triggers, in pixels: about one
 * window of rows at ~61px each. Expressed in pixels because the trigger reads
 * scroll geometry rather than row indices, and derived from the window size so
 * the intent stays "keep a window of unread rows ahead of the reader".
 */
const PREFETCH_LEAD_PX = ORG_MEMBERS_NEXT_LIMIT * 61;

/**
 * Nearest ancestor that scrolls vertically, or null when the page itself is the
 * scroller.
 *
 * Resolved by walking the tree rather than by selector or a passed-down ref
 * because this tab is lazy-mounted into a shell it does not own, and a selector
 * that stops matching would fail silently.
 */
const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  for (let el = node?.parentElement; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
  }
  return null;
};

function MembersTableSkeleton() {
  return (
    <div className="border border-ds-border-subtlest rounded-lg overflow-hidden bg-ds-surface-white shadow-sm">
      <Table className="min-w-[800px]">
        <TableHeader>
          <TableRow>
            {['Users', 'Email', 'Team', 'RBAC', 'Actions'].map((label) => (
              <TableHead
                key={label}
                // Pin the first column here too, or the skeleton and the real
                // table disagree on layout the moment data lands.
                className={cn(
                  'px-3',
                  label === 'Users' &&
                    'sticky left-0 z-20 bg-ds-surface-white border-r border-ds-border-subtlest'
                )}
              >
                {label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 6 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell className="px-3 py-3 sticky left-0 z-10 bg-ds-surface-white group-hover:bg-ds-gray-subtlest border-r border-ds-border-subtlest">
                <div className="flex items-center gap-3">
                  <Skeleton className="size-8 rounded-md shrink-0" />
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="px-3 py-3">
                <Skeleton className="h-4 w-40" />
              </TableCell>
              <TableCell className="px-3 py-3">
                <Skeleton className="h-8 w-28 rounded-md" />
              </TableCell>
              <TableCell className="px-3 py-3">
                <Skeleton className="h-9 w-32 rounded-[10px]" />
              </TableCell>
              <TableCell className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="size-9 rounded-[10px]" />
                  <Skeleton className="size-9 rounded-[10px]" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Values that are identical for every row in a given render (whether the
 * viewer is an admin, whether a role mutation is in flight, the trial-seat
 * cap). Bundled into one object - built once per render with `useMemo`, not
 * per row - so MemberRow takes one stable prop instead of four/five separate
 * booleans/numbers that all happen to share the same value across all rows.
 */
interface MemberRowContext {
  isAdmin: boolean;
  /** Viewer's org role; decides which roles the row's dropdown offers. */
  viewerRole: string | undefined;
  /** Viewer's own team; a team-scoped admin may only act within it. */
  viewerTeamUUID: string | undefined;
  isRolePending: boolean;
  showTrialAccess: boolean;
  seatCap: number;
  seatsFull: boolean;
}

interface MemberRowProps {
  user: OrgMemberRow;
  teams: OrgTeamFacet[];
  rowContext: MemberRowContext;
  isSelf: boolean;
  onRoleChange: (user: User, newRole: string) => void;
  onTeamChange: (user: OrgMemberRow, newTeam: string) => void;
  onCreateTeamClick: (user: OrgMemberRow) => void;
  onDeleteTeam: (team: any) => void;
  isToggling: boolean;
  onTrialAccessToggle: (user: User, enabled: boolean) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
}

/**
 * One row of the members table.
 *
 * Memoized because the row used to be inline JSX inside `sortedOrgArr.map`,
 * which meant every parent re-render (one keystroke settling, one trial-seat
 * toggle) reconciled all up to 200 loaded rows and every TeamSelector
 * popover, role dropdown, and Switch/Tooltip inside them; that reconciliation
 * was the dominant render cost, not any one row's own work. `memo` only pays
 * off because every prop here is referentially stable across an unrelated
 * parent re-render (primitives, or callbacks wrapped in `useCallback`) -
 * passing a mutation object or a freshly-computed array in would defeat it.
 */
const MemberRow = memo(function MemberRow({
  user,
  teams,
  rowContext,
  isSelf,
  onRoleChange,
  onTeamChange,
  onCreateTeamClick,
  onDeleteTeam,
  isToggling,
  onTrialAccessToggle,
  onEditUser,
  onDeleteUser,
}: MemberRowProps) {
  const {
    isAdmin,
    viewerRole,
    viewerTeamUUID,
    isRolePending,
    showTrialAccess,
    seatCap,
    seatsFull,
  } = rowContext;
  // No content-visibility on this row: it needs size containment, and per CSS
  // Containment Level 2 size containment does not apply to internal table boxes
  // other than table-cell, because the table layout algorithm cannot size a row
  // as if it were empty. On a <tr> the property is inert, so it bought nothing
  // and only read as an optimization that was not happening. Keeping off-screen
  // rows out of the DOM needs virtualization, which this table does not have.
  return (
    // `group`: the pinned cell below needs its own opaque background to hide
    // rows sliding under it, which would otherwise swallow the row hover.
    <TableRow className="group">
      {/* The identity column stays put while the rest scrolls sideways. Without
          it, scrolling right leaves rows identified only by email, and there is
          no hint that the table scrolls at all. */}
      <TableCell className="px-3 py-3 sticky left-0 z-10 bg-ds-surface-white group-hover:bg-ds-gray-subtlest border-r border-ds-border-subtlest">
        <div className="flex items-center gap-3">
          <Avatar className="size-8 rounded-md">
            {user.Image && (
              <AvatarImage
                src={user.Image}
                alt={`${user.FirstName} ${user.LastName}`}
                className="object-cover"
                loading="lazy"
                decoding="async"
              />
            )}
            <AvatarFallback className="rounded-md bg-ds-gray-subtler text-ds-text-subtle text-xs font-medium">
              {user.FirstName?.charAt(0)?.toUpperCase() ||
                user.GithubUsername?.charAt(0)?.toUpperCase() ||
                '?'}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-ds-text-darker">
              {user.FirstName &&
              user.FirstName !== 'null' &&
              user.FirstName !== 'undefined' &&
              user.FirstName !== user.GithubUsername
                ? `${user.FirstName} ${user.LastName && user.LastName !== 'null' && user.LastName !== 'undefined' ? user.LastName : ''}`.trim()
                : user.GithubUsername || user.GitlabUsername || 'Unknown User'}
            </div>
            <div className="text-sm text-ds-text-subtle">
              {user.GithubUsername || user.GitlabUsername || user.UserUUID}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="px-3 py-3 text-ds-text-darker text-sm">
        {user.Email}
      </TableCell>
      <TableCell className="px-3 py-3">
        <TeamSelector
          currentTeamName={user.TeamName || undefined}
          // Owners move anyone between teams and manage teams. An Admin can
          // only place a team-less person on their own team.
          teams={
            isOwner(viewerRole)
              ? teams
              : teams.filter((t) => t.TeamUUID === viewerTeamUUID)
          }
          isAdmin={
            isOwner(viewerRole) ||
            (isAdmin &&
              !isSelf &&
              !!viewerTeamUUID &&
              (!user.TeamUUID ||
                (user.TeamUUID === viewerTeamUUID && !isOwner(user.Role))))
          }
          canManageTeams={isOwner(viewerRole)}
          // Owners clear anyone's team; an Admin clears only a non-Owner on
          // their own team, never themselves (a team-less Admin would lose
          // their whole team view).
          canRemoveFromTeam={
            isOwner(viewerRole) ||
            (isAdmin &&
              !isSelf &&
              !!viewerTeamUUID &&
              user.TeamUUID === viewerTeamUUID &&
              !isOwner(user.Role))
          }
          onSelectTeam={(teamName) => onTeamChange(user, teamName)}
          onCreateTeam={() => onCreateTeamClick(user)}
          onDeleteTeam={onDeleteTeam}
        />
      </TableCell>
      <TableCell className="px-3 py-3">
        <div className="flex items-center gap-2">
          {(() => {
            // Nobody changes their own role: another Owner or Admin does it.
            const roleOptions = assignableRoles(
              viewerRole,
              { Role: user.Role, TeamUUID: user.TeamUUID },
              viewerTeamUUID
            );
            // An Admin can't see or grant Owner, so an Owner row stays locked
            // for them (the backend rejects it too).
            const isOwnerLocked =
              isOwner(user.Role) && !roleOptions.includes('owner');
            // A team-scoped Admin has no assignable role for most rows (any
            // Admin/Owner row, or a Member outside their own team) - without
            // this, the trigger stayed enabled over a zero-item menu, opening
            // an empty popup on click.
            const noAssignableRoles = roleOptions.length === 0;
            const roleDisabled =
              isRolePending ||
              !isAdmin ||
              isSelf ||
              isOwnerLocked ||
              noAssignableRoles;
            const roleTitle = !isAdmin
              ? 'Only Owners and Admins can change roles'
              : isSelf
                ? "You can't change your own role"
                : isOwnerLocked
                ? "Only Owners can change an Owner's role"
                : noAssignableRoles
                  ? 'Admins can only change Members and Admins on their own team'
                  : 'Change role';
            // Wrapped in a Tooltip, not the native `title`: a disabled
            // control never fires the browser's title tooltip, which would
            // leave non-admins and a sole admin with no explanation at all.
            return (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span className="inline-flex">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger
                          disabled={roleDisabled}
                          render={
                            <ShadcnButton
                              type="button"
                              variant="outline"
                              disabled={roleDisabled}
                              className="flex items-center justify-between w-36"
                            >
                              <span>{roleLabel(user.Role) || '-'}</span>
                              <ChevronDown className="w-4 h-4 text-ds-text-subtle shrink-0" />
                            </ShadcnButton>
                          }
                        />
                        <DropdownMenuContent align="start" className="w-36">
                          <DropdownMenuRadioGroup
                            value={user.Role}
                            onValueChange={(value) => onRoleChange(user, value)}
                          >
                            {roleOptions.map((role) => (
                              <DropdownMenuRadioItem key={role} value={role}>
                                {roleLabel(role)}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </span>
                  }
                />
                <TooltipContent side="top">{roleTitle}</TooltipContent>
              </Tooltip>
            );
          })()}
        </div>
      </TableCell>
      {showTrialAccess &&
        (() => {
          // Admins the workspace auto-seats keep access no
          // matter what — they are what guarantees someone can
          // always reach this page to hand seats back out.
          const isLockedAdmin = user.CanToggleTrialAccess === false;
          const hasSeat = !!user.HasTrialAccess;
          const blockedByCap = !hasSeat && seatsFull;
          const label = isLockedAdmin
            ? 'Admins always have access'
            : isSelf
              ? "You can't remove your own access"
              : blockedByCap
                ? seatCap === 0
                  ? 'This workspace has no trial seats'
                  : `All ${seatCap} trial seats are in use`
                : hasSeat
                  ? `Remove ${user.FirstName || 'this member'}'s trial access`
                  : `Give ${user.FirstName || 'this member'} trial access`;
          const isDisabled =
            isLockedAdmin || isSelf || isToggling || blockedByCap;
          return (
            <TableCell className="px-3 py-3">
              {/* Design-system Tooltip rather than a `title`
                  attribute: the native one is unstyled, slow to
                  appear, and never shows on a disabled control —
                  which is exactly when the reason matters most.
                  The wrapping span is the hover target so it
                  still triggers while the Switch is disabled. */}
              {/* No TooltipProvider here: this cell renders once
                  per row (up to 200), and a Provider inside the
                  row map would instantiate that many times. The
                  Table above already sits inside one. */}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span className="inline-flex items-center">
                      <Switch
                        checked={hasSeat || isLockedAdmin}
                        onCheckedChange={(checked: boolean) =>
                          onTrialAccessToggle(user, checked)
                        }
                        disabled={isDisabled}
                        aria-label={label}
                      />
                    </span>
                  }
                />
                <TooltipContent side="top">{label}</TooltipContent>
              </Tooltip>
            </TableCell>
          );
        })()}
      <TableCell className="px-3 py-3">
        <div className="flex items-center gap-2">
          <ShadcnButton
            variant="ghost"
            size="icon"
            onClick={() => onEditUser(user)}
            disabled={!isAdmin && !isSelf}
            title={
              !isAdmin && !isSelf
                ? 'Only administrators can edit other users'
                : 'Edit user'
            }
          >
            <PencilSimple />
          </ShadcnButton>
          <ShadcnButton
            variant="ghost"
            size="icon"
            onClick={() => onDeleteUser(user)}
            disabled={!isAdmin || isSelf}
            title={
              isSelf
                ? 'Cannot delete your own account'
                : !isAdmin
                  ? 'Only administrators can delete users'
                  : 'Delete user'
            }
          >
            <Trash className="text-ds-danger-default" />
          </ShadcnButton>
        </div>
      </TableCell>
    </TableRow>
  );
});
MemberRow.displayName = 'MemberRow';

/**
 * Search box for the members table.
 *
 * Owns the raw input value and the debounce itself, and tells the parent only
 * the settled term. Held in the parent, every keystroke re-rendered
 * TeamTabContent, and with it all 200 loaded rows and each row's team
 * combobox, role dropdown, seat switch and tooltip. That reconciliation, not
 * the request, is what made typing lag; debouncing the fetch never addressed
 * it. The parent now re-renders once per settled term instead of once per key.
 */
function MembersSearchInput({
  onSearchChange,
}: {
  onSearchChange: (value: string) => void;
}) {
  const [value, setValue] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchChangeRef = useRef(onSearchChange);

  // Kept current in an effect, not during render: assigning to ref.current while
  // rendering makes the render impure, which is what react-doctor's
  // no-ref-current-in-render catches. The ref exists so the debounce timer
  // always calls the latest callback without being re-created per render.
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Clear a pending timer on unmount so a settled term cannot land on a parent
  // that has already moved on.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  /*
   * The debounce lives on the handler, not on the value.
   *
   * Debouncing the value and then pushing it up from an effect makes the child
   * responsible for syncing parent state, which fires an extra render pass and
   * is the pattern react-doctor flags as no-prop-callback-in-effect. Typing is
   * an event, so the notification belongs on the event path.
   */
  const handleChange = (next: string) => {
    setValue(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onSearchChangeRef.current(next);
    }, 300);
  };

  return (
    <div className="relative max-w-64">
      <Input
        type="text"
        placeholder="Search"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        className="pl-10"
      />
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-subtle"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
  );
}

type SubTabId =
  | 'team'
  | 'cursor'
  | 'claude'
  | 'codex'
  | 'jira'
  | 'devrev'
  | 'asana'
  | 'slack';

function subTabFromAppname(appname?: string | null): SubTabId | null {
  const normalizedAppname = appname?.toLowerCase();
  if (normalizedAppname === 'jira' || normalizedAppname === 'linear') {
    return 'jira';
  }
  if (normalizedAppname === 'devrev') return 'devrev';
  if (normalizedAppname === 'asana') return 'asana';
  if (normalizedAppname === 'cursor') return 'cursor';
  if (normalizedAppname === 'claude') return 'claude';
  return null;
}

function TeamTabContent({ className, appname }: SettingsTabProps) {
  // Anchor for resolving the scrolling ancestor that drives paging.
  const rootRef = useRef<HTMLDivElement>(null);
  const [userBeingEdited, setUserBeingEdited] = useState<User | null>(null);
  const [userBeingDeleted, setUserBeingDeleted] = useState<User | null>(null);
  const [userBeingAssigned, setUserBeingAssigned] = useState<User | null>(null);
  const [prefilledTeamName, setPrefilledTeamName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [teamBeingDeleted, setTeamBeingDeleted] = useState<any>(null);
  // Only the settled search term lives here. MembersSearchInput owns the raw
  // value, so typing does not re-render this component or the table.
  const [searchTerm, setSearchTerm] = useState('');
  // Filter by UUID, not name. Names are renamable, and the previous name-based
  // filter resolved a team then fuzzy-matched each user's email or git username
  // against its member list, which silently dropped anyone whose records did not
  // line up even though their teamUUID said they belonged.
  const [selectedTeamUUID, setSelectedTeamUUID] = useState('');
  // Auth state for sync functionality
  const { authUrl } = useAuthStore();
  const [authSuccess, setAuthSuccess] = useState(false);

  // Get search params for URL parameter handling
  const searchParams = useSearchParams();
  const router = useRouter();
  const { data: session } = useSession();

  // React Query setup
  const toast = useToast();
  const organization = useOrganizationStore.getState().organization;
  const { user: currentUser } = useUserLogged();

  // Live store snapshots, read every render on purpose: they are the identity
  // signal this memo keys on, while the body below re-reads the full user and
  // org objects via getState() itself. Held in variables rather than inlined
  // in the dependency array so the deps stay statically checkable.
  const headerUserUuid = useUserStore.getState().user?.UserUUID;
  const headerOrgUuid = useOrganizationStore.getState().organization?.OrgUUID;

  // Memoized header object for API calls
  const headerObj = useMemo(() => {
    const user = useUserStore.getState().user;
    const organization = useOrganizationStore.getState().organization;

    return {
      ...emptyHeaderObj,
      Useruuid: headerUserUuid,
      Useremail: user?.Email,
      Username: user?.FirstName,
      Orgname: organization?.OrgName,
      Orguuid: headerOrgUuid,
    };
    // The two uuids are the invalidation signal. The body re-reads the full user
    // and org objects via getState() so the header never goes stale, and keying
    // on the uuids keeps it from rebuilding on unrelated store writes.
  }, [headerUserUuid, headerOrgUuid]);

  // Check if Slack is configured
  const { data: isSlackConfigured } = useSlackConfigured(organization?.OrgUUID);

  const { hasIntegration } = useOrgAuthInfo();

  const isAsanaConnected = hasIntegration('asana');

  // Use centralized mutations from useTeamData
  const updateUserRoleMutation = useUpdateUserRole(organization?.OrgUUID);
  const createTeamMutation = useCreateTeam(organization?.OrgUUID);
  const deleteTeamMutation = useDeleteTeam(organization?.OrgUUID);
  const addUserToTeamMutation = useAddUserToTeam(organization?.OrgUUID);
  const removeUserFromTeamMutation = useRemoveUserFromTeam(
    organization?.OrgUUID
  );
  const createUserMutation = useCreateUser(organization?.OrgUUID, headerObj);
  const deleteUserMutation = useDeleteUser(
    organization?.OrgUUID,
    currentUser?.accessToken
  );
  // `updateUserRoleMutation` itself is a new object every render (useMutation
  // spreads its result into a fresh wrapper each call); this primitive is
  // what actually stays stable in value between renders where it hasn't
  // changed, which is what MemberRow's memo needs.
  const isRolePending = updateUserRoleMutation.isPending;

  // Trial seats. `Enforced` already means "capped and still inside the trial
  // window", so it is the only condition needed here — the backend switches it
  // off the moment the trial ends. Deliberately not also testing !IsPaid: an
  // org that converts mid-trial keeps its cap for the rest of the window, and
  // hiding the column from those admins would leave seats enforced with no way
  // to manage them.
  const { trialAccess } = useTrialAccessStatus(
    organization?.OrgUUID,
    headerObj
  );
  const setTrialAccessMutation = useSetTrialAccess(
    organization?.OrgUUID,
    headerObj
  );
  const [togglingUserUUID, setTogglingUserUUID] = useState<string | null>(null);
  // Hoisted for MemberRow: a primitive prop the memoized row can compare by
  // value, instead of every row re-deriving it from `currentUser`.
  const isAdmin = isAdminOrOwner(currentUser?.Role);
  const showTrialAccess =
    isAdminOrOwner(currentUser?.Role) && trialAccess.Enforced;
  // A cap of 0 is a real, enforced state ("nobody is reviewed"), distinct from
  // null, which means seats are not enforced at all. Testing seatCap > 0 folded
  // the two together and left every switch enabled at cap 0, so a click sent a
  // grant the backend was always going to refuse.
  const capIsSet =
    trialAccess.SeatCap !== null && trialAccess.SeatCap !== undefined;
  const seatCap = trialAccess.SeatCap ?? 0;
  const seatsUsed = trialAccess.SeatsUsed;
  const seatsFull = capIsSet && seatsUsed >= seatCap;
  // A workspace can legitimately hold more grants than its cap: the rollout
  // backfill grandfathers everyone in rather than locking out people already
  // using the product, and a cap can be lowered later. Surfacing it lets an
  // admin reconcile instead of wondering why reviews stopped for some people.
  const overSeatCap = capIsSet && seatsUsed > seatCap;
  // Modal states
  const {
    isOpen: editIsOpen,
    onOpen: editOnOpen,
    onClose: editOnClose,
  } = useDisclosure();
  const {
    isOpen: createTeamIsOpen,
    onOpen: createTeamOnOpen,
    onClose: createTeamOnClose,
  } = useDisclosure();
  const {
    isOpen: addGitOrgIsOpen,
    onOpen: addGitOrgOnOpen,
    onClose: addGitOrgOnClose,
  } = useDisclosure();
  const {
    isOpen: teamMembersIsOpen,
    onOpen: teamMembersOnOpen,
    onClose: teamMembersOnClose,
  } = useDisclosure();
  const {
    isOpen: deleteConfirmIsOpen,
    onOpen: deleteConfirmOnOpen,
    onClose: deleteConfirmOnClose,
  } = useDisclosure();
  const {
    isOpen: deleteTeamConfirmIsOpen,
    onOpen: deleteTeamConfirmOnOpen,
    onClose: deleteTeamConfirmOnClose,
  } = useDisclosure();
  const {
    isOpen: addMemberIsOpen,
    onOpen: addMemberOnOpen,
    onClose: addMemberOnClose,
  } = useDisclosure();

  /*
   * One paginated request replaces this tab's whole mount fan-out: it used to
   * fetch every user, every team, then one request per team (22 requests on a
   * 16-team org, ~600KB on a 1,352-member one). Search and the team filter run
   * in the database now, and each row arrives with its own team name.
   *
   * The search term arrives already debounced from MembersSearchInput, which
   * also keeps keystrokes from re-rendering this component and the table.
   */
  const {
    members: orgArr,
    teams,
    total: totalMembers,
    provider: orgProvider,
    isLoading,
    isRefreshing,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useOrgMembers({
    search: searchTerm,
    teamUUID: selectedTeamUUID,
  });

  // The provider rides the first page of the members response, so the separate
  // getProvider request this tab used to fire on every mount is gone.
  const provider = orgProvider || null;

  /*
   * Org-wide admin-or-owner count: powers the edit modal's last-admin guard,
   * and the role dropdown's self-lockout guard (below). `fetchOrgMembers`'s
   * `role` filter is an exact match, so an owner is not returned by
   * `role: 'admin'` - two requests (one per role) summed via `.total` cover
   * both, matching `isAdminOrOwner`'s definition of "admin". Fetched whenever
   * either could need it - the modal is open, or the viewer is an admin who
   * might see their own row's dropdown - not on every mount for non-admins.
   */
  const { data: adminCount, refetch: refetchAdminCount } = useQuery({
    queryKey: ['orgMembers', 'adminOrOwnerCount', organization?.OrgUUID],
    queryFn: () =>
      Promise.all([
        fetchOrgMembers({ offset: 0, limit: 1, role: 'admin' }),
        fetchOrgMembers({ offset: 0, limit: 1, role: 'owner' }),
      ]).then(([adminPage, ownerPage]) => adminPage.total + ownerPage.total),
    enabled: (editIsOpen || isAdmin) && !!organization?.OrgUUID,
    staleTime: 60 * 1000,
  });

  // Bundled into one object, memoized once here, rather than six separate
  // props on MemberRow: the value is identical for every row in a given
  // render, so one stable reference shared across all rows both keeps
  // MemberRow's own prop list under react-doctor's boolean-prop-count rule
  // and avoids recomputing/re-passing the same six values 200 times.
  const rowContext = useMemo(
    () => ({
      isAdmin,
      viewerRole: currentUser?.Role,
      viewerTeamUUID: currentUser?.TeamUUID,
      isRolePending,
      showTrialAccess,
      seatCap,
      seatsFull,
    }),
    [
      isAdmin,
      currentUser?.Role,
      currentUser?.TeamUUID,
      isRolePending,
      showTrialAccess,
      seatCap,
      seatsFull,
    ]
  );

  // Dynamic sub-tabs based on provider — must be resolved before scroll paging
  // reads `activeSubTab`.
  const subTabs = useMemo(() => {
    // Tool and tracker mappings (Cursor, Claude Code, Codex, Jira/Linear,
    // DevRev, Asana, Slack) are Admin/Owner-only; a Member sees Members.
    if (!isAdmin) return [{ id: 'team' as SubTabId, label: 'Members' }];
    const tabs = [
      { id: 'team' as SubTabId, label: 'Members' },
      { id: 'cursor' as SubTabId, label: 'Cursor' },
      { id: 'claude' as SubTabId, label: 'Claude Code' },
      { id: 'codex' as SubTabId, label: 'Codex' },
    ];

    if (provider === 'JIRA' || provider === 'LINEAR') {
      const label = provider === 'LINEAR' ? 'Linear' : 'Jira';
      tabs.push({ id: 'jira' as SubTabId, label });
    }

    if (provider === 'DEVREV') {
      tabs.push({ id: 'devrev' as SubTabId, label: 'DevRev' });
    }

    if (provider === 'ASANA' || isAsanaConnected) {
      tabs.push({ id: 'asana' as SubTabId, label: 'Asana' });
    }

    if (isSlackConfigured) {
      tabs.push({ id: 'slack' as SubTabId, label: 'Slack' });
    }

    return tabs;
  }, [isAdmin, provider, isSlackConfigured, isAsanaConnected]);

  const [pageTabRaw, setPageTab] = useQueryState('page-tab');

  const activeSubTab = useMemo((): SubTabId => {
    const fromAppname = subTabFromAppname(appname);
    if (fromAppname && subTabs.some((t) => t.id === fromAppname)) {
      return fromAppname;
    }
    const normalized =
      pageTabRaw?.toLowerCase() === 'linear' ? 'jira' : pageTabRaw;
    const match = subTabs.find((t) => t.id === normalized);
    return (match?.id ?? subTabs[0]?.id ?? 'team') as SubTabId;
  }, [appname, pageTabRaw, subTabs]);

  /*
   * Scroll-driven paging: fetch the next window once the bottom of the page is
   * within roughly one window of rows.
   *
   * Deliberately NOT an IntersectionObserver on an anchor row. Three observer
   * variants each had a hole, because an observer only reports intersection
   * *transitions*:
   *   - It watched a real row, and this table renders inside
   *     <TabsContent value="team">, which Base UI unmounts when another sub-tab
   *     is selected. Switching away detached the watched row and switching back
   *     mounted a new one, so paging died until a full reload.
   *   - A fast scroll could pass the anchor while the observer was torn down for
   *     the duration of a fetch, so the transition never happened and the reader
   *     was stranded at the bottom with more data available.
   *   - The anchor row moves as windows land, so the ref detaches from one row
   *     and attaches to another in the same commit, which is fragile ordering to
   *     depend on.
   *
   * A distance-to-bottom reading has none of those properties: it is derived
   * from current geometry every scroll frame rather than from an event we might
   * miss, it holds no element reference, and the leading `check()` re-evaluates
   * on mount, on remount, and after each window lands, so the buffer refills
   * itself and being already at the bottom triggers immediately.
   */
  useEffect(() => {
    // The members panel is keepMounted, so its effects keep running while
    // another sub-tab is showing. Without this gate, scrolling a different
    // tab's content would page this table in the background.
    if (activeSubTab !== 'team' || !hasNextPage) return;

    /*
     * Read the geometry of the element that actually scrolls. The Settings shell
     * is `overflow-hidden` and scrolls an inner container (SettingsShell), so the
     * window never scrolls on this page: a documentElement reading is a constant
     * zero distance to bottom, which pages the entire roster on mount without
     * anyone touching the scrollbar.
     */
    const scroller = findScrollParent(rootRef.current);

    let frame = 0;
    const check = () => {
      frame = 0;
      if (!hasNextPage || isFetchingNextPage) return;
      const distanceToBottom = scroller
        ? scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight)
        : document.documentElement.scrollHeight -
          (window.scrollY + window.innerHeight);
      if (distanceToBottom < PREFETCH_LEAD_PX) fetchNextPage();
    };
    // rAF-coalesced: scroll fires far more often than layout changes, and one
    // read per frame is enough to never miss the threshold.
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };

    const scrollTarget: HTMLElement | Window = scroller ?? window;
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    check();

    return () => {
      scrollTarget.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [activeSubTab, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Check for success/cancel from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roleChangeStatus = urlParams.get('role_change');

    if (roleChangeStatus === 'success') {
      toast({
        title: 'Role Change Successful',
        description: 'User role has been updated successfully!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (roleChangeStatus === 'cancelled') {
      toast({
        title: 'Role Change Cancelled',
        description: 'Role change was cancelled. No changes were made.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  // Sync functionality
  const fetchDataFromGitProvider = async (gitOrgName: string) => {
    const accessToken = session?.user?.accessToken;
    if (!accessToken) {
      return {
        success: false,
        teamsWithMembers: [],
        members: [],
      };
    }

    const gitProviderTeams =
      session?.user?.provider === 'github'
        ? await importTeamsFromGh(gitOrgName, accessToken)
        : await importTeamsFromGl(
            gitOrgName,
            accessToken,
            authUrl?.length > 0 ? authUrl : undefined
          );

    if (!gitProviderTeams?.teams) {
      return {
        success: false,
        teamsWithMembers: [],
        members: [],
      };
    }

    const teamsWithMembers: TeamTypeWithMembers[] = [];
    const allMembers: any[] = [];

    for (const team of gitProviderTeams.teams) {
      const membersResult =
        session?.user?.provider === 'github'
          ? await getTeamMembersFromGh(gitOrgName, team.GitTeamSlug)
          : await getTeamMembersFromGl(
              (team as any).id,
              accessToken,
              authUrl?.length > 0 ? authUrl : undefined
            );

      if (membersResult?.members) {
        teamsWithMembers.push({
          TeamName: (team as any).name,
          TeamMembers: membersResult.members,
          TeamUUID: '',
        });
        allMembers.push(...membersResult.members);
      }
    }

    // Also fetch organization members
    const orgMembersResult =
      session?.user?.provider === 'github'
        ? await getGithubOrgMembers(gitOrgName, accessToken)
        : await getGitlabGroupMembers(gitOrgName, accessToken, authUrl);

    let orgMembers: any[] = [];
    if (Array.isArray(orgMembersResult)) {
      orgMembers = orgMembersResult;
    } else if (orgMembersResult && Array.isArray(orgMembersResult.members)) {
      orgMembers = orgMembersResult.members;
    }

    // Merge team members with org members (avoid duplicates)
    const memberLogins = new Set(allMembers.map((m) => m.login));
    const uniqueOrgMembers = orgMembers.filter(
      (m) => !memberLogins.has(m.login)
    );
    const finalMembers = [...allMembers, ...uniqueOrgMembers];

    return {
      success: true,
      teamsWithMembers,
      members: finalMembers,
    };
  };

  // Returns false on any partial or total failure (a team create, or a
  // member create), so the caller can avoid reporting a misleading success.
  const syncGhDataAndStatesWithBackend = async (
    gitProviderTeams: TeamTypeWithMembers[],
    gitProviderMembers: any[]
  ): Promise<boolean> => {
    let fullySucceeded = true;
    const teamObject: any = {};
    if (gitProviderTeams.length > 0) {
      await Promise.all(
        gitProviderTeams.map(async (gitProviderTeam: TeamTypeWithMembers) => {
          const teamRes = await createTeam({
            OrgUUID: organization?.OrgUUID || '',
            TeamName: gitProviderTeam.TeamName,
          });
          // A failed create must not silently map this team's members to an
          // undefined TeamUUID -- they'd otherwise be created/moved onto no
          // team at all in the member-assignment pass below, with no signal
          // why.
          if (!teamRes?.TeamUUID) {
            toast({
              title: `Failed to import "${gitProviderTeam.TeamName}"`,
              description:
                teamRes?.Error ||
                teamRes?.error ||
                'Could not create this team. Try again from the roster.',
              status: 'error',
              duration: 8000,
              isClosable: true,
            });
            fullySucceeded = false;
            return;
          }
          const { TeamUUID } = teamRes;
          for (const member of gitProviderTeam.TeamMembers) {
            teamObject[member.login] = TeamUUID;
          }
          gitProviderTeam.TeamUUID = TeamUUID;
        })
      );
    }

    if (gitProviderMembers.length > 0) {
      await Promise.all(
        gitProviderMembers.map(async (gitProviderMember: any) => {
          const memberRes = await createUser(
            gitProviderMember.name,
            '',
            '',
            gitProviderMember.avatar_url,
            '',
            headerObj,
            teamObject[gitProviderMember?.login ?? ''] ?? '',
            organization?.OrgUUID,
            gitProviderMember.login,
            session?.user?.provider
          );
          // createUser catches its own failures and resolves with {Error}
          // rather than rejecting -- not inspecting the result let a failed
          // member creation still report the sync as fully succeeded,
          // marking the source synced with that member missing.
          if (!memberRes?.UserUUID || memberRes?.Error) {
            fullySucceeded = false;
            return;
          }
          gitProviderMember.userUUID = memberRes.UserUUID;
        })
      );
    }

    // Refresh data after sync
    // Note: refetch() would be called here if using React Query
    return fullySucceeded;
  };

  const handleSyncButtonClick = async (autoTrigger: boolean = false) => {
    if (!organization?.GitOrgName) {
      addGitOrgOnOpen();
    } else {
      const results = await Promise.all(
        organization?.GitOrgName.split(',').map(async (gitOrgName) => {
          return await fetchDataFromGitProvider(gitOrgName);
        })
      );
      const success = results.every((result) => result.success);
      if (!success) {
        if (!authSuccess && !autoTrigger) {
          toast({
            title: 'Permission Required',
            description:
              'Grant access to all the organizations to import data, redirecting to update your permissions.',
            status: 'info',
            duration: 6000,
            isClosable: true,
          });
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const providerName = session?.user?.provider;
          if (!providerName) {
            toast({
              title: 'Missing provider information',
              description: 'Unable to re-authenticate without a provider.',
              status: 'error',
              duration: 4000,
              isClosable: true,
            });
            return;
          }

          await signIn(
            providerName,
            { redirect: true, callbackUrl: '/settings?authSuccess=true' },
            {
              scope: mountScope(providerName),
              prompt: 'consent',
            }
          );
        }
        return;
      }
      const teamsWithMembers = results.flatMap(
        (result) => result.teamsWithMembers
      );
      const members = results.flatMap((result) => result.members);
      const syncFullySucceeded = await syncGhDataAndStatesWithBackend(
        teamsWithMembers,
        members
      );
      // Never mark the source synced, or report success, after a partial or
      // total failure: syncedGitSource gates the auto-retry effect off, and
      // the per-team/per-fetch failures above already showed their own
      // error toast -- a blanket "synced successfully" on top would
      // contradict it.
      if (syncFullySucceeded) {
        syncedGitSource(
          { OrgUUID: organization?.OrgUUID || '' },
          headerObj
        ).catch((err) => {});

        toast({
          title: 'Success',
          description: 'Team members synced successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // Relocated above handleTeamChange (its only caller): handleTeamChange is
  // wrapped in useCallback below and reads this in its dependency array, so
  // it must already be initialized by the time that array is evaluated.
  const createTeamAndAssign = useCallback(
    async (user: User, teamName: string) => {
      try {
        const response = await createTeamMutation.mutateAsync({
          TeamName: teamName,
          OrgUUID: organization?.OrgUUID || '',
        });

        // Parse response if it's a string
        let parsedResponse = response;
        if (typeof response === 'string') {
          try {
            parsedResponse = JSON.parse(response);
          } catch (parseError) {
            toast({
              title: 'Failed to create team',
              description: 'Invalid response from server',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            return;
          }
        }

        // Check for error in response
        if (parsedResponse?.Error) {
          toast({
            title: 'Failed to create team',
            description: parsedResponse.Error,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        // Get TeamUUID from response
        const teamUUID = parsedResponse?.TeamUUID;

        if (teamUUID) {
          try {
            // Wait for the team creation to complete before assigning user
            await addUserToTeamMutation.mutateAsync({
              teamId: teamUUID,
              userId: user.UserUUID,
              skipSuccessToast: true, // Skip the individual toast since we show a combined message
            } as any);

            toast({
              title: 'Team created and user assigned',
              description: `Created team "${teamName}" and assigned ${user.FirstName}`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          } catch (assignmentError) {
            toast({
              title: 'Team created but assignment failed',
              description:
                'The team was created but the user could not be assigned',
              status: 'warning',
              duration: 3000,
              isClosable: true,
            });
          }
        } else {
          toast({
            title: 'Team created but assignment failed',
            description:
              'The team was created but the user could not be assigned',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        toast({
          title: 'Failed to create team',
          description:
            error instanceof Error
              ? error.message
              : 'Could not create the new team',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally read the mutations' stable `.mutateAsync` method rather than the wrapper object, which useMutation() re-spreads into a new object every render
    [
      createTeamMutation.mutateAsync,
      addUserToTeamMutation.mutateAsync,
      organization?.OrgUUID,
      toast,
    ]
  );

  /**
   * The team a member currently belongs to.
   *
   * Reads the member's own TeamUUID, which is the column the backend actually
   * stores membership in. The previous version scanned every team's member list
   * comparing emails and git usernames, so a member whose provider records did
   * not line up read as "no team" and their reassignment silently skipped the
   * remove step, leaving them on two teams.
   */
  const currentTeamOf = useCallback(
    (user: OrgMemberRow) =>
      user.TeamUUID
        ? teams.find((team) => team.TeamUUID === user.TeamUUID)
        : undefined,
    [teams]
  );

  // Handler functions
  const handleTeamChange = useCallback(
    (user: OrgMemberRow, newTeam: string) => {
      // Only admins can change teams
      if (!isAdminOrOwner(currentUser?.Role)) {
        toast({
          title: 'Access Denied',
          description: 'Only administrators can change user teams.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Handle "Create Team" option
      if (newTeam === 'CREATE_TEAM') {
        setUserBeingAssigned(user);
        setPrefilledTeamName('');
        createTeamOnOpen();
        return;
      }

      // Handle "Create new team" with search query
      if (newTeam.startsWith('CREATE_NEW_')) {
        const teamName = newTeam.replace('CREATE_NEW_', '');
        createTeamAndAssign(user, teamName);
        return;
      }

      // Handle "No Team" option - remove user from current team
      if (newTeam === 'No Team') {
        const currentTeam = currentTeamOf(user);

        if (currentTeam) {
          removeUserFromTeamMutation.mutate({
            teamId: currentTeam.TeamUUID,
            userId: user.UserUUID,
          });

          toast({
            title: 'User removed from team',
            description: `${user.FirstName} has been removed from ${currentTeam.TeamName}`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'No team to remove from',
            description: `${user.FirstName} is not currently assigned to any team`,
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        }
        return;
      }

      // Find the team UUID for the selected team name
      const selectedTeam = teams.find((team) => team.TeamName === newTeam);
      if (selectedTeam) {
        const currentTeam = currentTeamOf(user);

        // If user is already in the selected team, do nothing
        if (currentTeam && currentTeam.TeamUUID === selectedTeam.TeamUUID) {
          toast({
            title: 'Already in team',
            description: `${user.FirstName} is already in ${selectedTeam.TeamName}`,
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
          return;
        }

        // If user is in a different team, remove them first, then add to new team
        if (currentTeam) {
          // Use the onSuccess callback to chain the mutations
          removeUserFromTeamMutation.mutate(
            {
              teamId: currentTeam.TeamUUID,
              userId: user.UserUUID,
            },
            {
              onSuccess: () => {
                // After successful removal, add to new team
                addUserToTeamMutation.mutate({
                  teamId: selectedTeam.TeamUUID,
                  userId: user.UserUUID,
                });
              },
              onError: (error) => {
                toast({
                  title: 'Failed to remove from team',
                  description: `Could not remove ${user.FirstName} from ${currentTeam.TeamName}`,
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                });
              },
            }
          );
        } else {
          // User is not in any team, just add to the selected team
          addUserToTeamMutation.mutate({
            teamId: selectedTeam.TeamUUID,
            userId: user.UserUUID,
          });
        }
      } else {
        toast({
          title: 'Team not found',
          description: `Could not find team: ${newTeam}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally read the mutations' stable `.mutate` method rather than the wrapper object, which useMutation() re-spreads into a new object every render
    [
      currentUser?.Role,
      toast,
      teams,
      currentTeamOf,
      createTeamAndAssign,
      setUserBeingAssigned,
      createTeamOnOpen,
      removeUserFromTeamMutation.mutate,
      addUserToTeamMutation.mutate,
    ]
  );

  const handleRoleChange = useCallback(
    async (user: User, newRole: string) => {
      // Only admins can change roles
      if (!isAdminOrOwner(currentUser?.Role)) {
        toast({
          title: 'Access Denied',
          description: 'Only Owners and Admins can change user roles.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Block self-demotion when you're the org's sole admin (real lockout
      // risk). `adminCount` above is cached for up to 60s and only gates
      // whether the dropdown is enabled - on submit, re-fetch it so a stale
      // read can't let the org's last admin demote themselves; fail closed
      // if the fresh count can't be confirmed as > 1.
      if (currentUser?.UserUUID === user.UserUUID) {
        const freshAdminCount = await refetchAdminCount()
          .then((result) => result.data)
          .catch(() => undefined);
        if (freshAdminCount === undefined || freshAdminCount <= 1) {
          toast({
            title: 'Cannot Change Own Role',
            description: "You're the only admin. Promote another member first.",
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      }

      // Convert role to lowercase for backend consistency
      const roleForBackend = newRole.toLowerCase();
      updateUserRoleMutation.mutate({ user, newRole: roleForBackend });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally read the mutation's stable `.mutate` method rather than the wrapper object, which useMutation() re-spreads into a new object every render
    [
      currentUser?.Role,
      currentUser?.UserUUID,
      refetchAdminCount,
      toast,
      updateUserRoleMutation.mutate,
    ]
  );

  /**
   * Grant or revoke one person's trial seat.
   *
   * Every rule here is also enforced server-side — this only spares the user a
   * round trip and gives a specific message instead of a generic refusal.
   */
  const handleTrialAccessToggle = useCallback(
    async (user: User, enabled: boolean) => {
      if (!isAdminOrOwner(currentUser?.Role)) {
        toast({
          title: 'Access Denied',
          description: 'Only administrators can change trial access.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (enabled && seatsFull && !user.HasTrialAccess) {
        toast({
          title:
            seatCap === 0
              ? 'This workspace has no trial seats'
              : 'All trial seats are in use',
          description: overSeatCap
            ? `Your trial covers ${seatCap} ${seatCap === 1 ? 'person' : 'people'} and ${seatsUsed} are switched on. Turn some off to get back under the limit.`
            : `Your trial covers ${seatCap} ${seatCap === 1 ? 'person' : 'people'}. Turn someone off to free up a seat.`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setTogglingUserUUID(user.UserUUID);
      try {
        await setTrialAccessMutation.mutateAsync({ user, enabled });
      } catch {
        // The mutation's onError already rolled the row back and toasted.
      } finally {
        setTogglingUserUUID(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally read the mutation's stable `.mutateAsync` method rather than the wrapper object, which useMutation() re-spreads into a new object every render
    [
      currentUser?.Role,
      seatsFull,
      seatCap,
      overSeatCap,
      seatsUsed,
      toast,
      setTrialAccessMutation.mutateAsync,
      setTogglingUserUUID,
    ]
  );

  const handleEditUser = useCallback(
    (user: User) => {
      setUserBeingEdited(user);
      editOnOpen();
    },
    [setUserBeingEdited, editOnOpen]
  );

  const handleDeleteUser = useCallback(
    (user: User) => {
      // Only admins can delete users
      if (!isAdminOrOwner(currentUser?.Role)) {
        toast({
          title: 'Access Denied',
          description: 'Only administrators can delete users.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Prevent users from deleting themselves
      if (currentUser?.UserUUID === user.UserUUID) {
        toast({
          title: 'Cannot Delete Own Account',
          description:
            'You cannot delete your own account. Ask another administrator to do this.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setUserBeingDeleted(user);
      deleteConfirmOnOpen();
    },
    [
      currentUser?.Role,
      currentUser?.UserUUID,
      toast,
      setUserBeingDeleted,
      deleteConfirmOnOpen,
    ]
  );

  const confirmDeleteUser = () => {
    if (userBeingDeleted) {
      // The member's own org, not the viewer's: the roster spans every
      // combined org, so this row may belong to a different one.
      deleteUserMutation.mutate({
        userUUID: userBeingDeleted.UserUUID,
        organizationUUID: userBeingDeleted.OrganizationUUID,
      });
      deleteConfirmOnClose();
      setUserBeingDeleted(null);
    }
  };

  const handleDeleteTeam = useCallback(
    (team: any) => {
      // Only Owners can delete teams; a team-scoped admin is denied even
      // though the row's dropdown still shows the option (shared with the
      // team-reassignment controls it is allowed to use).
      if (!isOwner(currentUser?.Role)) {
        toast({
          title: 'Access Denied',
          description: `${ownerOnly('delete teams')}.`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setTeamBeingDeleted(team);
      deleteTeamConfirmOnOpen();
    },
    [currentUser?.Role, toast, setTeamBeingDeleted, deleteTeamConfirmOnOpen]
  );

  const confirmDeleteTeam = () => {
    if (teamBeingDeleted) {
      deleteTeamMutation.mutate(teamBeingDeleted.TeamUUID);
      deleteTeamConfirmOnClose();
      setTeamBeingDeleted(null);
    }
  };

  const handleTeamCreated = (teamUUID: string) => {
    if (userBeingAssigned) {
      addUserToTeamMutation.mutate({
        teamId: teamUUID,
        userId: userBeingAssigned.UserUUID,
      });
      setUserBeingAssigned(null);
    }
  };

  // handleCreateTeamClick already receives the row's own user object (see
  // MemberRow), so, unlike the version this replaced, it needs no orgArr
  // lookup and its useCallback deps never change.
  const handleCreateTeamClick = useCallback(
    (user: OrgMemberRow) => {
      setUserBeingAssigned(user);
      // Clear any name left over from a prior inline "Create new team
      // <name>" attempt (see handleTeamChange) -- this is a distinct,
      // un-prefilled create flow.
      setPrefilledTeamName('');
      createTeamOnOpen();
    },
    [setUserBeingAssigned, createTeamOnOpen]
  );

  /*
   * Search, team filter and ordering all happen in the database now, so the
   * rows arrive ready to render. The client-side filter this replaced matched
   * users against each team's member list by email or git username, which
   * dropped anyone whose records did not line up even when their teamUUID said
   * they belonged; the server filters on that column directly.
   */
  const sortedOrgArr = orgArr;

  const renderTeamContent = () => {
    /*
     * No early return on isLoading. The search input lives in the branch below,
     * so replacing this whole block with a skeleton destroys and recreates that
     * input, which drops the caret mid-typing. The skeleton now stands in for
     * the table only, further down.
     */
    if (error) {
      console.error(error);
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-ds-danger-default text-sm text-center">
              We couldn&apos;t load users. Please try again in a moment.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 motion-stagger">
        {/* Header with title, user count, and controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-between w-full">
            {/* Search bar */}
            <MembersSearchInput onSearchChange={setSearchTerm} />
            <div className="flex items-center gap-3">
              <Select
                value={selectedTeamUUID}
                onValueChange={(value) => {
                  if (value != null) setSelectedTeamUUID(value);
                }}
                items={[
                  { value: '', label: 'All Teams' },
                  { value: NO_TEAM, label: 'No Team' },
                  ...teams.map((team) => ({
                    value: team.TeamUUID,
                    label: team.TeamName,
                  })),
                ]}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Teams</SelectItem>
                  <SelectItem value={NO_TEAM}>No Team</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.TeamUUID} value={team.TeamUUID}>
                      {team.TeamName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(isOwner(currentUser?.Role) ||
                (currentUser?.Role === 'admin' && !currentUser?.TeamUUID)) && (
                <ShadcnButton
                  type="button"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    // Clear any name left over from a prior inline "Create
                    // new team <name>" attempt (see handleTeamChange) --
                    // this toolbar button is a distinct, un-prefilled flow.
                    setPrefilledTeamName('');
                    createTeamOnOpen();
                  }}
                >
                  <FaPlus />
                  Create Team
                </ShadcnButton>
              )}
              {isAdminOrOwner(currentUser?.Role) && (
                <ShadcnButton type="button" onClick={addMemberOnOpen}>
                  <FaPlus />
                  Add user
                </ShadcnButton>
              )}
              {isOwner(currentUser?.Role) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <ShadcnButton
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleSyncButtonClick(false)}
                        >
                          <FaSync />
                        </ShadcnButton>
                      }
                    />
                    <TooltipContent side="top">
                      {`Sync Team Members with ${getProviderDisplayName(organization, currentUser?.provider)}`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>

        {/* User table */}
        {isLoading ? (
          <MembersTableSkeleton />
        ) : sortedOrgArr.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Users size={24} weight="regular" />
              </EmptyMedia>
              {/* A search or team filter that matches nobody is a different
                  situation from an org with no members, and the wrong copy
                  there reads as data loss. */}
              {searchTerm || selectedTeamUUID ? (
                <>
                  <EmptyTitle>No members match</EmptyTitle>
                  <EmptyDescription>
                    Try a different name, email, or team.
                  </EmptyDescription>
                </>
              ) : (
                <>
                  <EmptyTitle>No members yet</EmptyTitle>
                  <EmptyDescription>
                    Add a user or sync your team to get started.
                  </EmptyDescription>
                </>
              )}
            </EmptyHeader>
          </Empty>
        ) : (
          <div
            // Results for a new search or filter are in flight while the
            // current rows stay put. A quiet dim says "working" without
            // swapping the table out from under the search input.
            aria-busy={isRefreshing}
            className={cn(
              'border border-ds-border-subtlest rounded-lg overflow-hidden bg-ds-surface-white shadow-sm transition-opacity',
              isRefreshing && 'opacity-60'
            )}
          >
            {/* One provider for every row's tooltip instead of one per row
                (up to 200 instances): TooltipProvider only needs to be an
                ancestor, not local to each trigger. */}
            <TooltipProvider>
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-3 sticky left-0 z-20 bg-ds-surface-white border-r border-ds-border-subtlest">
                      Users
                    </TableHead>
                    <TableHead className="px-3">Email</TableHead>
                    <TableHead className="px-3">Team</TableHead>
                    <TableHead className="px-3">RBAC</TableHead>
                    {showTrialAccess && (
                      <TableHead className="px-3">
                        <span className="flex items-center gap-2">
                          Trial Access
                          {/* Monospace so the count reads as a metric and the
                            digits stay aligned as seats are toggled. Over-limit
                            is a warning, not a danger: the workspace is above
                            its cap but nothing is broken — reviews still run
                            for the earliest seat holders. */}
                          <span
                            className={cn(
                              'ds-text-code-sm',
                              overSeatCap
                                ? 'text-ds-warning-default'
                                : 'text-ds-text-subtle'
                            )}
                          >
                            {seatsUsed}/{seatCap}
                            {overSeatCap && ' · over limit'}
                          </span>
                        </span>
                      </TableHead>
                    )}
                    <TableHead className="px-3">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOrgArr.map((user) => (
                    <MemberRow
                      key={user.UserUUID}
                      user={user}
                      teams={teams}
                      rowContext={rowContext}
                      isSelf={currentUser?.UserUUID === user.UserUUID}
                      onRoleChange={handleRoleChange}
                      onTeamChange={handleTeamChange}
                      onCreateTeamClick={handleCreateTeamClick}
                      onDeleteTeam={handleDeleteTeam}
                      isToggling={togglingUserUUID === user.UserUUID}
                      onTrialAccessToggle={handleTrialAccessToggle}
                      onEditUser={handleEditUser}
                      onDeleteUser={handleDeleteUser}
                    />
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>
            {isFetchingNextPage && (
              <div className="flex flex-col gap-3 p-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-full" />
                ))}
              </div>
            )}
            {!hasNextPage && (
              <p
                aria-live="polite"
                className="border-t border-ds-border-subtlest p-3 text-center ds-text-body-sm text-ds-text-subtle"
              >
                Showing all {totalMembers}{' '}
                {totalMembers === 1 ? 'member' : 'members'}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={className} ref={rootRef}>
      <div className="space-y-6">
        {/* Header */}
        <SettingsHeader
          title="Team Management"
          description="Manage your organization members, teams, and integration mappings."
        />

        {/* Sub-tab Navigation */}
        <Tabs
          value={activeSubTab}
          onValueChange={(v) => {
            void setPageTab(v);
          }}
        >
          <TabsList className="mb-6">
            {subTabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                onMouseEnter={() => SUB_TAB_PRELOAD[tab.id]?.()}
                onFocus={() => SUB_TAB_PRELOAD[tab.id]?.()}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* The members query gates the members tab only. Integration sub-tabs
              mount immediately; each tab owns its own loading state. */}
          {activeSubTab === 'team' && isLoading ? (
            <MembersTableSkeleton />
          ) : (
            <>
              {/* keepMounted: Base UI unmounts an inactive panel by default,
                  and a large org leaves ~1,350 rows here, each with a combobox,
                  a role menu, a seat switch and a tooltip. Tearing all of that
                  down on every sub-tab switch and rebuilding it on the way back
                  is what makes switching feel slow. Hidden panels cost no layout
                  or paint, so keeping this one mounted trades memory for the
                  teardown spike. It is a mitigation, not the fix: bounding the
                  mounted row count needs virtualization. */}
              <TabsContent value="team" keepMounted>
                {renderTeamContent()}
              </TabsContent>
              <TabsContent value="cursor">
                <CursorTab />
              </TabsContent>
              <TabsContent value="claude">
                <ClaudeTab />
              </TabsContent>
              <TabsContent value="codex">
                <CodexTab />
              </TabsContent>
              <TabsContent value="jira">
                <JiraLinearTab />
              </TabsContent>
              <TabsContent value="devrev">
                <DevRevTab />
              </TabsContent>
              <TabsContent value="asana">
                <AsanaTab />
              </TabsContent>
              <TabsContent value="slack">
                <SlackMappingTab />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* Modals - Same as original UserManagement */}
      <EditUserModal
        editIsOpen={editIsOpen}
        userBeingEdited={userBeingEdited as User}
        setUserBeingEdited={setUserBeingEdited}
        editOnClose={editOnClose}
        orgArr={orgArr}
        setOrgArr={() => {}} // TODO: Implement with React Query mutation
        setTeamsWithMembers={() => {}} // TODO: Implement with React Query mutation
        teamsWithMembers={[]}
        adminCount={adminCount}
      />

      <CreateTeamModal
        isOpen={createTeamIsOpen}
        onClose={createTeamOnClose}
        // The facets carry counts, not member lists. The modal resolves a
        // user's current team from their own TeamUUID, so an empty list here is
        // accurate rather than lossy.
        teams={teams.map((team) => ({ ...team, TeamMembers: [] }))}
        setTeams={(_newTeams) => {
          // Teams are managed by React Query and will be automatically refetched
          // due to query invalidation in the modal, so no manual update needed
        }}
        userToAssign={userBeingAssigned}
        initialTeamName={prefilledTeamName}
        onTeamCreated={handleTeamCreated}
      />

      <AddGitOrg
        isOpen={addGitOrgIsOpen}
        onClose={addGitOrgOnClose}
        // Linking an org only writes the workspace scope. Its repositories stay
        // invisible until the PR bot is installed on them, so carry the user to
        // Code Management with the install prompt open rather than ending on a
        // success toast that reads like the job is done.
        //
        // GitHub only: the prompt leads to the GitHub App install page. GitLab
        // and Bitbucket grant repo access through a Group Access Token or App
        // Password in their own modal, so sending them here would be a dead end.
        onOrgAdded={() => {
          if (session?.user?.provider !== 'github') return;
          router.push('/settings?tab=code-management&install_prompt=1');
        }}
      />

      <TeamMembersModal
        isOpen={teamMembersIsOpen}
        onClose={teamMembersOnClose}
        team={selectedTeam}
        setTeams={() => {}} // TODO: Implement with React Query mutation
        // Unreachable in this tab: nothing calls teamMembersOnOpen and
        // selectedTeam is never set, so this modal cannot open. The member
        // picker inside it would need its own paged fetch if it ever does.
        orgArr={orgArr}
        setSelectedTeam={setSelectedTeam}
      />

      {/* Add Member Modal */}
      <AddMemberModal isOpen={addMemberIsOpen} onClose={addMemberOnClose} />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmIsOpen}
        onOpenChange={(open) => !open && deleteConfirmOnClose()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Are you sure you want to delete{' '}
            <span className="font-bold">
              {userBeingDeleted?.FirstName} {userBeingDeleted?.LastName}
            </span>
            ? This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <ShadcnButton variant="ghost" onClick={deleteConfirmOnClose}>
              Cancel
            </ShadcnButton>
            <ShadcnButton variant="destructive" onClick={confirmDeleteUser}>
              Delete User
            </ShadcnButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Confirmation Modal */}
      <Dialog
        open={deleteTeamConfirmIsOpen}
        onOpenChange={(open) => !open && deleteTeamConfirmOnClose()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete Team</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Are you sure you want to delete the team{' '}
            <span className="font-bold">{teamBeingDeleted?.TeamName}</span>?
            This will remove all users from this team and cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <ShadcnButton variant="ghost" onClick={deleteTeamConfirmOnClose}>
              Cancel
            </ShadcnButton>
            <ShadcnButton variant="destructive" onClick={confirmDeleteTeam}>
              Delete Team
            </ShadcnButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TeamTab(props: SettingsTabProps) {
  return (
    <Suspense fallback={<MembersTableSkeleton />}>
      <TeamTabContent {...props} />
    </Suspense>
  );
}
